import { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/integrations/supabase/client';

export interface ChatChannel {
  id: string;
  company_id: string;
  name: string | null;
  type: 'direct' | 'group';
  created_by: string;
  created_at: string;
  // computed
  displayName: string;
  lastMessage?: string;
  lastMessageAt?: string;
  unreadCount: number;
}

export interface ChatMessage {
  id: string;
  channel_id: string;
  sender_id: string;
  content: string;
  created_at: string;
  senderName: string;
}

export interface ColleagueProfile {
  id: string;
  full_name: string;
  avatar_url?: string;
}

export function useInternalChat() {
  const { user } = useAuth();
  const [channels, setChannels] = useState<ChatChannel[]>([]);
  const [colleagues, setColleagues] = useState<ColleagueProfile[]>([]);
  const [messages, setMessages] = useState<Record<string, ChatMessage[]>>({});
  const [loading, setLoading] = useState(true);
  const profileCache = useRef<Record<string, string>>({});

  const companyId = user?.company_id;
  const userId = user?.id;

  // Fetch profiles for name resolution
  const fetchProfiles = useCallback(async () => {
    if (!companyId) return;
    const { data: companyUsers } = await supabase
      .from('company_users')
      .select('user_id')
      .eq('company_id', companyId)
      .eq('is_active', true);
    if (!companyUsers) return;

    const userIds = companyUsers.map(cu => cu.user_id);
    const { data: profiles } = await supabase
      .from('profiles')
      .select('id, full_name, avatar_url')
      .in('id', userIds);

    if (profiles) {
      setColleagues(profiles.filter(p => p.id !== userId) as ColleagueProfile[]);
      profiles.forEach(p => { profileCache.current[p.id] = p.full_name || 'Ukendt'; });
    }
  }, [companyId, userId]);

  // Fetch channels with unread counts
  const fetchChannels = useCallback(async () => {
    if (!companyId || !userId) return;

    const { data: memberships } = await (supabase as any)
      .from('chat_channel_members')
      .select('channel_id, last_read_at')
      .eq('user_id', userId);

    if (!memberships || memberships.length === 0) {
      setChannels([]);
      setLoading(false);
      return;
    }

    const channelIds = memberships.map((m: any) => m.channel_id);
    const lastReadMap: Record<string, string> = {};
    memberships.forEach((m: any) => { lastReadMap[m.channel_id] = m.last_read_at; });

    const { data: channelRows } = await (supabase as any)
      .from('chat_channels')
      .select('*')
      .in('id', channelIds);

    if (!channelRows) { setLoading(false); return; }

    // Get all members for DM name resolution
    const { data: allMembers } = await (supabase as any)
      .from('chat_channel_members')
      .select('channel_id, user_id')
      .in('channel_id', channelIds);

    // Get latest message per channel + unread counts
    const enriched: ChatChannel[] = await Promise.all(
      channelRows.map(async (ch: any) => {
        // Display name
        let displayName = ch.name || 'Gruppe';
        if (ch.type === 'direct' && allMembers) {
          const otherUserId = allMembers.find(
            (m: any) => m.channel_id === ch.id && m.user_id !== userId
          )?.user_id;
          if (otherUserId) {
            displayName = profileCache.current[otherUserId] || 'Kollega';
          }
        }

        // Latest message
        const { data: latest } = await (supabase as any)
          .from('chat_messages')
          .select('content, created_at')
          .eq('channel_id', ch.id)
          .order('created_at', { ascending: false })
          .limit(1);

        // Unread count
        const lastRead = lastReadMap[ch.id];
        const { count } = await (supabase as any)
          .from('chat_messages')
          .select('id', { count: 'exact', head: true })
          .eq('channel_id', ch.id)
          .gt('created_at', lastRead)
          .neq('sender_id', userId);

        return {
          ...ch,
          displayName,
          lastMessage: latest?.[0]?.content,
          lastMessageAt: latest?.[0]?.created_at,
          unreadCount: count || 0,
        } as ChatChannel;
      })
    );

    enriched.sort((a, b) => {
      const aTime = a.lastMessageAt || a.created_at;
      const bTime = b.lastMessageAt || b.created_at;
      return new Date(bTime).getTime() - new Date(aTime).getTime();
    });

    setChannels(enriched);
    setLoading(false);
  }, [companyId, userId]);

  // Fetch messages for a channel
  const getMessages = useCallback(async (channelId: string) => {
    const { data } = await (supabase as any)
      .from('chat_messages')
      .select('*')
      .eq('channel_id', channelId)
      .order('created_at', { ascending: true })
      .limit(100);

    if (data) {
      const enriched = data.map((m: any) => ({
        ...m,
        senderName: profileCache.current[m.sender_id] || 'Ukendt',
      }));
      setMessages(prev => ({ ...prev, [channelId]: enriched }));
    }
  }, []);

  // Send message
  const sendMessage = useCallback(async (channelId: string, content: string) => {
    if (!companyId || !userId) return;
    await (supabase as any).from('chat_messages').insert({
      channel_id: channelId,
      company_id: companyId,
      sender_id: userId,
      content,
    });
    // Update last_read_at
    await (supabase as any)
      .from('chat_channel_members')
      .update({ last_read_at: new Date().toISOString() })
      .eq('channel_id', channelId)
      .eq('user_id', userId);
  }, [companyId, userId]);

  // Mark channel as read
  const markRead = useCallback(async (channelId: string) => {
    if (!userId) return;
    await (supabase as any)
      .from('chat_channel_members')
      .update({ last_read_at: new Date().toISOString() })
      .eq('channel_id', channelId)
      .eq('user_id', userId);
  }, [userId]);

  // Create or find DM
  const createDM = useCallback(async (otherUserId: string): Promise<string | null> => {
    if (!companyId || !userId) return null;

    // Find existing direct channel between these two users
    const { data: myChannels } = await (supabase as any)
      .from('chat_channel_members')
      .select('channel_id')
      .eq('user_id', userId);

    if (myChannels) {
      for (const mc of myChannels) {
        const { data: ch } = await (supabase as any)
          .from('chat_channels')
          .select('id, type')
          .eq('id', mc.channel_id)
          .eq('type', 'direct')
          .maybeSingle();

        if (ch) {
          const { data: otherMember } = await (supabase as any)
            .from('chat_channel_members')
            .select('user_id')
            .eq('channel_id', ch.id)
            .eq('user_id', otherUserId)
            .maybeSingle();

          if (otherMember) return ch.id;
        }
      }
    }

    // Create new channel
    const { data: newChannel } = await (supabase as any)
      .from('chat_channels')
      .insert({
        company_id: companyId,
        type: 'direct',
        created_by: userId,
      })
      .select('id')
      .single();

    if (!newChannel) return null;

    await (supabase as any).from('chat_channel_members').insert([
      { channel_id: newChannel.id, user_id: userId },
      { channel_id: newChannel.id, user_id: otherUserId },
    ]);

    await fetchChannels();
    return newChannel.id;
  }, [companyId, userId, fetchChannels]);

  // Realtime subscription
  useEffect(() => {
    if (!companyId) return;

    const channel = supabase
      .channel('internal-chat')
      .on(
        'postgres_changes',
        { event: 'INSERT', schema: 'public', table: 'chat_messages' },
        (payload) => {
          const msg = payload.new as any;
          if (msg.company_id !== companyId) return;

          const enrichedMsg: ChatMessage = {
            ...msg,
            senderName: profileCache.current[msg.sender_id] || 'Ukendt',
          };

          setMessages(prev => {
            const existing = prev[msg.channel_id] || [];
            // Deduplicate: skip if ID matches or same content+sender within 5s
            const duplicate = existing.some(m =>
              m.id === msg.id ||
              (m.content === msg.content && m.sender_id === msg.sender_id &&
               Math.abs(new Date(m.created_at).getTime() - new Date(msg.created_at).getTime()) < 5000)
            );
            if (duplicate) {
              // Replace optimistic placeholder with real message
              return {
                ...prev,
                [msg.channel_id]: existing.map(m =>
                  m.content === msg.content && m.sender_id === msg.sender_id && m.id !== msg.id
                    ? enrichedMsg : m
                ),
              };
            }
            return { ...prev, [msg.channel_id]: [...existing, enrichedMsg] };
          });

          // Refresh channel list for unread counts
          fetchChannels();
        }
      )
      .subscribe();

    return () => { supabase.removeChannel(channel); };
  }, [companyId, fetchChannels]);

  // Initial load
  useEffect(() => {
    fetchProfiles().then(() => fetchChannels());
  }, [fetchProfiles, fetchChannels]);

  const totalUnread = channels.reduce((sum, ch) => sum + ch.unreadCount, 0);

  return {
    channels,
    colleagues,
    messages,
    loading,
    totalUnread,
    getMessages,
    sendMessage,
    markRead,
    createDM,
    refreshChannels: fetchChannels,
  };
}
